// MCO stress grid — same chrome as InsuranceOptimizeDataGrid (axis label row, header
// gradient, cell padding). Separate component: MCO math is margin (not indemnity).
// Colors reuse getCellColor: $0 = red, payment scales green.

import Alert from "@mui/material/Alert";
import Divider from "@mui/material/Divider";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { styled, useColorScheme } from "@mui/material/styles";
import { stressCellAt } from "api-toolkit/mco/mcoStressGrid";
import { getCellColor, getHeaderColor } from "components/StressTestVisualization/getCellColor";
import { formatNumber, formatPrice } from "utility/formatters";

const PRICE_FMT = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  currencySign: "accounting",
  maximumFractionDigits: 2,
});
const PAY_FMT = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  currencySign: "accounting",
  maximumFractionDigits: 0,
});

const formatAxisPrice = (v) => (v || v === 0 ? PRICE_FMT.format(v)?.replace("$", "$ ") : "");
const formatPay = (v) => (v || v === 0 ? PAY_FMT.format(v)?.replace("$", "$ ") : "");

const CustomWidthTooltip = styled(({ className, ...props }) => (
  <Tooltip
    {...props}
    classes={{ popper: className }}
    enterDelay={1000}
    enterTouchDelay={1000}
    enterNextDelay={1000}
  />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: "none",
  },
});

function cellHoverTitle(cell, fy, hp) {
  if (!cell?.ready) return "";
  return (
    <>
      <Typography>
        <strong>County Yields:</strong>
        <br />
        &nbsp;&nbsp;County Yield: {fy}
        <br />
        <br />
        <br />
        <strong>Prices:</strong>
        <br />
        &nbsp;&nbsp;Price: {formatAxisPrice(hp)}
        <br />
      </Typography>
      <Divider />
      <Typography>
        <strong>MCO:</strong>
        <br />
        &nbsp;&nbsp;Trigger margin: {formatPrice(cell.triggerMargin, 2)}
        <br />
        &nbsp;&nbsp;Harvest margin: {formatPrice(cell.harvestMargin, 2)}
        <br />
        &nbsp;&nbsp;Payment factor: {formatNumber(cell.paymentFactor, 2)}
        <br />
        &nbsp;&nbsp;Protection $/ac: {formatPrice(cell.protectionPerAcre, 2)}
        <br />
      </Typography>
      <Divider />
      <Typography>
        <strong>MCO payment $/ac:</strong> {formatPrice(cell.mcoPayment, 2)}
        <br />
      </Typography>
    </>
  );
}

export default function McoStressTestTable({ mco }) {
  const { mode } = useColorScheme();
  const isDarkMode = mode !== "light";
  const grid = mco.stressGrid;

  if (!grid?.ready) {
    return (
      <Alert severity="info">
        {grid?.blockers?.join("; ") || "MCO stress grid unavailable — need Official actuarial data."}
      </Alert>
    );
  }

  // Low → high on both axes (same reading order as InsuranceOptimizeDataGrid).
  const yields = [...grid.rows].sort((a, b) => a - b);
  const prices = [...grid.cols].sort((a, b) => a - b);
  const maxPay = Math.max(0, ...grid.cells.map((c) => c.mcoPayment || 0));

  return (
    <TableContainer sx={{ boxSizing: "border-box", maxWidth: 1050 }} className="insuranceOptimizeDataGrid">
      <Table
        padding="none"
        spacing={0}
        size="small"
        sx={{
          [`& .${tableCellClasses.root}`]: {
            borderBottom: "none",
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
          },
        }}
      >
        <TableBody>
          <TableRow>
            <TableCell
              colSpan={1}
              sx={{
                textAlign: "center",
                padding: "0 !important",
                paddingTop: "4pt !important",
                paddingBottom: "4pt !important",
                paddingRight: "4pt !important",
                paddingLeft: "4pt !important",
                backgroundColor: "background.paper",
                borderRadius: "8px 0 0 0",
              }}
            >
              Price
            </TableCell>
            <TableCell
              colSpan={yields.length}
              sx={{
                textAlign: "center",
                backgroundColor: "background.paper",
                borderRadius: "0 8px 0 0",
              }}
            >
              Yield: Low → High
            </TableCell>
          </TableRow>

          <TableRow
            sx={{
              borderBottom: "1px solid",
              borderColor: "colors.borderLight",
            }}
          >
            <TableCell sx={{ padding: "0 !important", backgroundColor: "background.paper" }}>&nbsp;</TableCell>
            {yields.map((fy, index) => (
              <TableCell
                key={fy}
                sx={{
                  textAlign: "right",
                  padding: "0 !important",
                  paddingTop: "4pt !important",
                  paddingBottom: "4pt !important",
                  paddingRight: "4pt !important",
                  backgroundColor: getHeaderColor(index, yields.length, isDarkMode),
                  fontSize: { xs: "0.75rem", sm: "0.75rem" },
                }}
              >
                {formatNumber(fy, 0)}
              </TableCell>
            ))}
          </TableRow>

          {prices.map((hp, rowIndex) => {
            const isLastRow = rowIndex === prices.length - 1;
            return (
              <TableRow key={hp}>
                <TableCell
                  sx={{
                    borderRight: "1px solid",
                    borderColor: "colors.borderLight",
                    p: 0,
                    textAlign: "center",
                    paddingTop: "0 !important",
                    paddingBottom: "0 !important",
                    paddingLeft: "8px !important",
                    paddingRight: "8px !important",
                    maxWidth: { xs: "6rem", md: "5rem", lg: "4rem" },
                    backgroundColor: getHeaderColor(rowIndex, prices.length, isDarkMode),
                    fontSize: "0.75rem",
                    borderRadius: isLastRow ? "0 0 0 8px" : "0",
                  }}
                >
                  {formatAxisPrice(hp)}
                </TableCell>
                {yields.map((fy, colIndex) => {
                  const cell = stressCellAt(grid, fy, hp);
                  const pay = cell?.ready ? cell.mcoPayment : null;
                  const isLastCell = colIndex === yields.length - 1;
                  return (
                    <CustomWidthTooltip key={fy} sx={{ maxWidth: 1000 }} arrow title={cellHoverTitle(cell, fy, hp)}>
                      <TableCell
                        sx={{
                          paddingLeft: "4pt !important",
                          paddingRight: "8pt !important",
                          paddingTop: "8pt !important",
                          paddingBottom: "8pt !important",
                          textAlign: "right",
                          backgroundColor: cell?.ready ? getCellColor(pay, 0, true, maxPay, 0, isDarkMode) : "",
                          borderRadius: isLastRow && isLastCell ? "0 0 8px 0" : "0",
                        }}
                      >
                        {cell?.ready ? formatPay(pay) : "--"}
                      </TableCell>
                    </CustomWidthTooltip>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
